import { FC, memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { Alert, Col, Row, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { AppointmentHistoryDto } from '../../../../../shared/dtos/appointment.dto';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { formatHours } from '../../../util/duration-utils';
import { toDateTime } from '../../../util/date-utils';

const AppointmentHistory: FC = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [history, setHistory] = useState<AppointmentHistoryDto[]>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result: AppointmentHistoryDto[] = await authFetch('/api/v1/appointment/therapist/history');
      setHistory(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const renderRow = (appointment: AppointmentHistoryDto, index: number): ReactElement => {

    return (
      <tr key={`appointment-${index}`}>
        <td>{appointment.patient}</td>
        <td>{toDateTime(appointment.date).toFormat('dd.MM.yyyy')}</td>
        <td>{toDateTime(appointment.startTime).toFormat('hh:mm')}</td>
        <td>{toDateTime(appointment.endTime).toFormat('hh:mm')}</td>
        <OverlayTrigger
          placement="top"
          delay={{ show: 150, hide: 100 }}
          overlay={<Tooltip>{formatHours(appointment.durationInHours)}</Tooltip>}>
          <td>{appointment.durationInHours.toFixed(2)}h</td>
        </OverlayTrigger>
      </tr>
    );
  };

  return (
    <div>
      <Row>
        <Col md={12}>
          <h2 className="fw-semibold mt-4">
            {i18next.t('appointmentHistory.title')}
          </h2>

          {isLoading && (
            <div className="d-flex justify-content-center align-items-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          )}

          {!isLoading && errorMessage && (
            <Alert variant="danger" className="mt-3">
              {errorMessage}
            </Alert>
          )}

          {!isLoading && !errorMessage && (
            <>
              {history.length === 0 ? (
                <div className="text-muted mt-3">
                  {i18next.t('appointmentHistory.noData')}
                </div>
              ) : (
                <div className="table-responsive mt-3">
                  <table className="table table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>{i18next.t('appointmentHistory.patient')}</th>
                        <th>{i18next.t('appointmentHistory.date')}</th>
                        <th>{i18next.t('appointmentHistory.startTime')}</th>
                        <th>{i18next.t('appointmentHistory.endTime')}</th>
                        <th>{i18next.t('appointmentHistory.duration')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(renderRow)}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );



};

export default memo(AppointmentHistory);
